import { HttpStatusCode } from 'axios';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import config from '../../../config/config';
import authService from '../../../config/auth.service';
import axiosInstance from '../../../config/interceptors/axios.interceptor';
import { countryMappings, currencyMappings } from '../../../constants/dataConstants';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { withUtag } from '../../../utils/analytics';

export const TokenHandler = () => {
  const [queryParameters] = useSearchParams();
  const selectedLang = useSelector((state: RootState) => state.language.currentLanguage);

  const checkForMyAccountBetaAccess = (decodedAccessToken: any) => {
    const isBetaUser = decodedAccessToken?.Permission?.some(
      (featureObj: any) => featureObj?.f?.toLowerCase() === 'myaccountbeta' && featureObj?.r === 1
    );
    if (isBetaUser) {
      localStorage.setItem('showMyAccSpecialColumns', 'true');
    } else {
      localStorage.setItem('showMyAccSpecialColumns', 'false');
    }
  };

  const checkForAPFeatureAccess = (decodedAccessToken: any) => {
    const isAPVisible = decodedAccessToken?.Permission?.some(
      (featureObj: any) => featureObj?.f?.toLowerCase() === 'approfile' && featureObj?.r === 1
    );
    if (isAPVisible) {
      localStorage.setItem('isAccessibleAPFeature', 'true');
    } else {
      localStorage.setItem('isAccessibleAPFeature', 'false');
    }
  };

  const checkFoAssignmentSectionFeatureAccess = (decodedAccessToken: any) => {
    const isAssignmentSectionVisible = decodedAccessToken?.Permission?.some(
      (featureObj: any) => featureObj?.f?.toLowerCase() === 'assignment' && featureObj?.r === 1
    );
    if (isAssignmentSectionVisible) {
      localStorage.setItem('isAccessibleAssignmentSection', 'true');
    } else {
      localStorage.setItem('isAccessibleAssignmentSection', 'false');
    }
  };

  const checkForHATVFeatureAccess = (decodedAccessToken: any) => {
    const isHATVVisible = decodedAccessToken?.Permission?.some(
      (featureObj: any) => featureObj?.f?.toLowerCase() === 'hatv' && featureObj?.r === 1
    );
    if (isHATVVisible) {
      localStorage.setItem('isAccessibleHATVFeature', 'true');
    } else {
      localStorage.setItem('isAccessibleHATVFeature', 'false');
    }
  };

  const checkForReportBuilderFeatureAccess = (decodedAccessToken: any) => {
    const isReportVisible = decodedAccessToken?.Permission?.some(
      (featureObj: any) => featureObj?.f?.toLowerCase() === 'reportbuilder' && featureObj?.r === 1
    );
    if (isReportVisible) {
      localStorage.setItem('isAccessibleReportBuilderFeature', 'true');
    } else {
      localStorage.setItem('isAccessibleReportBuilderFeature', 'false');
    }
  };

  const checkForConversationalAIFeatureAccess = (decodedAccessToken: any) => {
    const isConverseAIVisible = decodedAccessToken?.Permission?.some(
      (featureObj: any) => featureObj?.f?.toLowerCase() === 'conversationai' && featureObj?.r === 1
    );

    if (isConverseAIVisible) {
      localStorage.setItem('isAccessibleConverseAIFeature', 'true');
    } else {
      localStorage.setItem('isAccessibleConverseAIFeature', 'false');
    }
  };

  const checkforABOProfileSummaryLoaFeatureAccess = (decodedAccessToken: any) => {
    const isABOProfileSummaryVisible = decodedAccessToken?.Permission?.some(
      (featureObj: any) =>
        featureObj?.f?.toLowerCase() === 'aboprofilesummary-loa' && featureObj?.r === 1
    );

    if (isABOProfileSummaryVisible) {
      localStorage.setItem('isAccessibleAboProfileSummaryLoa', 'true');
    } else {
      localStorage.setItem('isAccessibleAboProfileSummaryLoa', 'false');
    }
  };

  const checkforABOGrowthCompanionFeatureAccess = (decodedAccessToken: any) => {
    const isABOGrowthCampanionVisible = decodedAccessToken?.Permission?.some(
      (featureObj: any) =>
        featureObj?.f?.toLowerCase() === 'abogrowthcompanion' && featureObj?.r === 1
    );

    if (isABOGrowthCampanionVisible) {
      localStorage.setItem('isAccessibleABOGrowthCompanion', 'true');
    } else {
      localStorage.setItem('isAccessibleABOGrowthCompanion', 'false');
    }
  };

  useEffect(() => {
    async function getTokenExchange() {
      const lgsAuthorizationCode = queryParameters.get('code');
      const id = queryParameters.get('id');
      const app = queryParameters.get('app');
      const url = `${config.appUrl}/auth/token?code=${lgsAuthorizationCode}&id=${id}&app=${app}`;

      try {
        const tokenResponse = await axiosInstance.get(url);

        if (
          tokenResponse &&
          tokenResponse.data &&
          tokenResponse.data.statusCode === HttpStatusCode.Ok
        ) {
          const token = await authService.handleTokenResponse(tokenResponse.data);
          const decodedAccessToken = authService.decodeAccessToken(token);
          localStorage.setItem('userAffiliateCode', decodedAccessToken?.AffiliateCode ?? '');
          localStorage.setItem('userCountryCode', decodedAccessToken?.CountryCode ?? '');
          localStorage.setItem(
            'userRoleType',
            JSON.stringify(decodedAccessToken?.PermissionGroup ?? [])
          );

          if (token) {
            checkForAPFeatureAccess(decodedAccessToken);
            checkForHATVFeatureAccess(decodedAccessToken);
            checkForReportBuilderFeatureAccess(decodedAccessToken);
            checkForConversationalAIFeatureAccess(decodedAccessToken);
            checkforABOProfileSummaryLoaFeatureAccess(decodedAccessToken);
            checkforABOGrowthCompanionFeatureAccess(decodedAccessToken);
            checkFoAssignmentSectionFeatureAccess(decodedAccessToken);
            checkForMyAccountBetaAccess(decodedAccessToken);

            // TODO: Remove this after the AI Summaries feature is enabled
            localStorage.setItem('isAISummariesAccessible', 'false');

            const userNativeId = localStorage.getItem('auth-nativeId');
            const userRoleType = JSON.parse(localStorage.getItem('userRoleType') || '[]');
            const identifyByUtag = {
              user_aiu: userNativeId?.toLowerCase(),
              user_role: userRoleType,
            };

            withUtag().then((utag) => utag.view(identifyByUtag));

            const siteCountryCode = localStorage.getItem('userCountryCode');
            const siteTags = {
              site_country: siteCountryCode ? countryMappings[siteCountryCode] : '',
              site_currencyCode: siteCountryCode ? currencyMappings[siteCountryCode] : '',
              site_language: selectedLang,
            };

            window.utag_data = siteTags;
            const pageUrl = `${config.appUrl}/am/landingpage`;
            const landingResponse = await axiosInstance.get(pageUrl);
            if (landingResponse?.data?.body?.permissionGroupType) {
              const landingPage = landingResponse?.data?.body?.permissionGroupType;
              switch (landingPage) {
                case 1:
                case 2:
                case 3:
                case 5:
                  window.location.href = '/myaccount';
                  break;
                case 4:
                case 6:
                  window.location.href = '/hatv';
                  break;
                default:
                  window.location.href = '/myaccount';
                  break;
              }
            } else {
              window?.heap?.resetIdentity();
              window.location.href = '/login';
            }
            // window.location.href = "/myaccount";
          }
        }
      } catch (err) {
        console.error(`Get token exchange error: ${err}`);
      }
    }
    getTokenExchange();
  }, []);

  return <div>Loading.....</div>;
};
